import { ArrowRight01Icon, Store02Icon } from '@hugeicons/react'
import { Autoplay } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import { useEventSlug } from '@/shared/hooks/useEventSlug'
import { useNavigation } from '@/shared/hooks/useNavigation'
import { useRecords } from '@/shared/hooks/useRecords'
import { sortByPosition } from '@/shared/lib/array/sort-by-position'
import Image from '@/shared/ui/AppImage'
import { Button } from '@/shared/ui/Button'
import { Card } from '@/shared/ui/Card'
import { GameIcon } from '@/shared/ui/GameIcon'

import { BlockEmptyState } from '../BlockEmptyState'
import { BlockSkeleton } from '../BlockSkeleton'

import type { BlockComponentProps } from '../index'

import 'swiper/css'

const DEFAULT_LIMIT = 8
const PLACEHOLDER_IMAGE = '/placeholders/reward.jpg'

interface RewardItemRecord {
  coin?: number | null
  active?: boolean
  quantity?: number
  quantity_sold?: number
  image?: { file?: { url?: string | null } | null } | null
}

interface RewardRecord {
  id: number
  name?: string | null
  active?: boolean
  position?: number | null
  image?: { file?: { url?: string | null } | null } | null
  reward_items?: RewardItemRecord[]
}

const getRewardImageUrl = (reward: RewardRecord) =>
  reward.image?.file?.url ??
  reward.reward_items?.find(item => item.image?.file?.url)?.image?.file?.url ??
  PLACEHOLDER_IMAGE

const getRewardCoin = (reward: RewardRecord) => {
  const item = reward.reward_items?.find(entry => entry.active !== false)
  return item?.coin ?? null
}

const isRewardSoldOut = (reward: RewardRecord) => {
  const items = reward.reward_items?.filter(entry => entry.active !== false) ?? []
  if (items.length === 0) return false

  return items.every(
    item =>
      typeof item.quantity === 'number' &&
      typeof item.quantity_sold === 'number' &&
      item.quantity_sold >= item.quantity
  )
}

/**
 * Destaque compacto da lojinha na home: carrossel horizontal de produtos em
 * cards quadrados e CTA para o catálogo completo.
 */
export const StoreBlock = ({ block }: BlockComponentProps) => {
  const eventSlug = useEventSlug()
  const { navigateTo } = useNavigation()
  const limit = block.settings?.limit ?? DEFAULT_LIMIT

  const { records, isLoading } = useRecords({
    r: eventSlug ? `/user/digital_products/${eventSlug}/rewards` : '',
    params: {
      pagination: { per_page: limit }
    },
    shouldFetch: !!eventSlug
  })

  if (isLoading) return <BlockSkeleton rows={2} />

  const rewards = [...((records ?? []) as RewardRecord[])]
    .filter(reward => reward.active !== false)
    .sort(sortByPosition)
    .slice(0, limit)

  if (rewards.length === 0)
    return (
      <BlockEmptyState
        icon={Store02Icon}
        title="Nenhum produto na lojinha"
        hint="Os produtos publicados aparecem aqui assim que a lojinha for configurada."
        action={
          <Button
            variant="primary"
            size="sm"
            className="w-full"
            onClick={() => navigateTo('/lojinha')}
            trailingIcon={<ArrowRight01Icon size={14} aria-hidden />}
          >
            Ir para a lojinha
          </Button>
        }
      />
    )

  return (
    <Card className="card-gamification overflow-hidden">
      <div className="bg-[linear-gradient(135deg,color-mix(in_srgb,var(--color-brand)_18%,white),color-mix(in_srgb,var(--color-brand)_6%,white))] px-4 pb-2 pt-3">
        <h2 className="m-0 flex items-center gap-2 text-caps font-bold uppercase tracking-wide text-gray-800">
          <Store02Icon size={14} variant="duotone" aria-hidden />
          {block.title || 'Lojinha'}
        </h2>
      </div>

      <Card.Body className="flex flex-col gap-3 px-4 pb-4 pt-2">
        <Swiper
          modules={[Autoplay]}
          slidesPerView="auto"
          spaceBetween={10}
          loop={rewards.length > 3}
          autoplay={{
            delay: 3200,
            disableOnInteraction: false,
            pauseOnMouseEnter: true
          }}
          className="!overflow-visible"
        >
          {rewards.map(reward => {
            const soldOut = isRewardSoldOut(reward)
            const coin = getRewardCoin(reward)

            return (
              <SwiperSlide key={reward.id} className="!w-[5.5rem]">
                <button
                  type="button"
                  onClick={() => navigateTo('/lojinha')}
                  className="flex w-full cursor-pointer flex-col gap-1.5 border-0 bg-transparent p-0 text-left"
                >
                  <span className="relative block aspect-square overflow-hidden rounded-xl border border-[color-mix(in_srgb,var(--color-brand)_18%,white)] bg-white shadow-sm">
                    <Image
                      src={getRewardImageUrl(reward)}
                      alt={reward.name ?? ''}
                      width={88}
                      height={88}
                      className="h-full w-full object-cover"
                    />
                    {coin != null && (
                      <span className="absolute bottom-1 left-1 flex items-center gap-0.5 rounded-full bg-black/70 px-1.5 py-0.5 text-[10px] font-bold text-white">
                        <GameIcon name="coin" size={9} />
                        {coin}
                      </span>
                    )}
                    {soldOut && (
                      <span className="absolute inset-0 flex items-center justify-center bg-black/45 text-[10px] font-bold uppercase tracking-wide text-white">
                        Esgotado
                      </span>
                    )}
                  </span>
                  <span className="line-clamp-2 text-[11px] font-semibold leading-tight text-gray-800">
                    {reward.name}
                  </span>
                </button>
              </SwiperSlide>
            )
          })}
        </Swiper>

        <Button
          variant="primary"
          size="sm"
          className="w-full shadow-sm"
          onClick={() => navigateTo('/lojinha')}
          trailingIcon={<ArrowRight01Icon size={14} aria-hidden />}
        >
          Ver lojinha
        </Button>
      </Card.Body>
    </Card>
  )
}
